import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Calendar,
  CheckCircle,
  CreditCard,
  FileText,
  GitHub,
  Linkedin,
  Map,
  MessageCircle,
  Music,
  Tool,
  Youtube,
} from "react-feather";
import rogerioImg from "../images/foto_perfil.png";
import { PAID_PRODUCTS } from "../data/products";
import { SitePage, SiteShell } from "../components/sitePrimitives";
import { TestimonialsGrid } from "../components/Testimonials";
import { getVariant } from "../lib/experiments";
import { trackEvent } from "../lib/analytics";

// ─── TOKENS ────────────────────────────────────────────────────────────────
const C = {
  bg: "#17253D",
  bg2: "#1D2C48",
  surface: "#1F3050",
  surface2: "#284067",
  card: "rgba(31, 48, 80, 0.82)",
  border: "rgba(148,163,184,0.12)",
  borderHover: "rgba(34,184,209,0.34)",
  accent: "#22B8D1",
  accent2: "#6C8FB0",
  accent3: "#22C55E",
  warning: "#F59E0B",
  accentDim: "rgba(34,184,209,0.10)",
  accentBorder: "rgba(34,184,209,0.18)",
  textPrimary: "#F4F7FC",
  textSecondary: "#C7D2E8",
  textMuted: "#93A0BE",
  textDimmer: "#7482A0",
  textSoft: "#57648A",
  mono: '"JetBrains Mono", monospace',
  display: '"Bitter", serif',
};

// ─── DATA ──────────────────────────────────────────────────────────────────
// Hero único (antes era um carrossel de 3 mensagens diferentes rodando
// sozinho a cada poucos segundos: mentoria, currículo e planos disputando o
// mesmo espaço). Os 3 caminhos continuam na página - agora como os 3
// primeiros cards de "O que você encontra aqui", logo abaixo - mas o
// primeiro bloco da home fixa uma única promessa, sem competir consigo
// mesma pelos 5 segundos de atenção de quem chega.
// T4 do backlog de conversao: "control" e a promessa atual; "aggressive" e a
// reescrita proposta na auditoria de oferta (duvida -> aprovacao, transformacao
// mais especifica). getVariant() sorteia uma vez por visitante (ver
// src/lib/experiments.js) - o resto do hero (bullets, CTAs, icone) fica igual
// nas duas variantes, só o texto de headline/descrição muda.
const heroHeadlineVariants = {
  control: {
    title: "Tenha clareza sobre seu próximo passo em QA.",
    highlight: "próximo passo em QA.",
    description:
      "Diagnóstico do seu momento, ferramentas com IA pra praticar e mentoria individual com quem já contratou e trabalhou em QA - tudo em um só lugar, sem juntar pedaços soltos sozinho.",
  },
  aggressive: {
    title: "Pare de estudar QA sem saber se está no caminho.",
    highlight: "no caminho.",
    description:
      "Mentoria individual com quem já contratou e trabalhou em QA, mais ferramentas com IA pra praticar toda semana - pra sair da dúvida sobre por onde começar e chegar preparado na entrevista.",
  },
};

const heroMain = {
  eyebrow: "QA Iniciante",
  bullets: ["Diagnóstico do seu momento", "Ferramentas com IA pra praticar", "Mentoria e revisão de currículo"],
  to: "/workspace-qa",
  cta: "Descobrir meu próximo passo — é grátis",
  secondaryTo: "/assinaturas",
  secondaryCta: "Ver planos",
  icon: Calendar,
  color: C.accent,
};

const quickHighlights = [
  "Mentoria individual",
  "Revisão de currículo",
  "E-books de QA",
  "Ferramentas com IA",
  "Roadmap de carreira",
];

const navigationCards = [
  {
    title: "Mentoria",
    description: "Direcionamento individual para acelerar sua evolução com plano, prática e foco.",
    to: "/mentoria",
    icon: Calendar,
    tag: "Direção",
  },
  {
    title: "Revisão de currículo",
    description: "Otimize seu currículo e LinkedIn para se destacar em processos seletivos de QA.",
    to: "/revisao-curriculo",
    icon: FileText,
    tag: "Carreira",
  },
  {
    title: "Planos",
    description: "Encontre o plano ideal para seus objetivos, com acesso a benefícios e recursos exclusivos.",
    to: "/assinaturas",
    icon: CreditCard,
    tag: "Acesso",
  },
  {
    title: "E-books",
    description: "Guias e leituras para construir repertório em testes, carreira e automação.",
    to: "/ebooks",
    icon: BookOpen,
    tag: "Estudo",
  },
  {
    title: "Ferramentas",
    description: "Use IA para gerar casos de teste, organizar entregas e otimizar seu fluxo de trabalho.",
    to: "/produtos",
    icon: Tool,
    tag: "Prática",
  },
  {
    title: "Roadmap QA",
    description: "Uma visão simples do que estudar, em qual ordem e como começar do jeito certo.",
    to: "/mapa-de-carreira-qa",
    icon: Map,
    tag: "Trilha",
  },
];

const journeySteps = [
  {
    num: "01",
    title: "Entenda sua fase",
    description: "Identifique seu momento: você precisa de base, prática, um portfólio forte ou direção para vagas?",
  },
  {
    num: "02",
    title: "Estude com ordem",
    description: "Siga um caminho claro com e-books e roadmap, evitando a sobrecarga de informações.",
  },
  {
    num: "03",
    title: "Pratique e organize",
    description: "Aplique o conhecimento com templates, ferramentas de IA e projetos práticos.",
  },
  {
    num: "04",
    title: "Acelere com direção",
    description: "Receba orientação com mentoria, revisão de currículo e o apoio da comunidade.",
  },
];

const mentorshipBenefits = [
  "Diagnóstico do seu momento atual na carreira",
  "Plano de estudos enxuto e possível de seguir",
  "Direção para criar portfólio e projetos de QA",
  "Dúvidas sobre testes manuais, automação e mercado",
  "Ajustes de posicionamento para buscar oportunidades",
];

const resumeBenefits = [
  "Revisão do currículo com foco em vagas de QA",
  "Melhoria de descrição de experiências e projetos",
  "Ajuste de palavras-chave para recrutadores",
  "Orientação para LinkedIn e GitHub",
  "Sugestões práticas para deixar seu perfil mais forte",
];

const planCards = [
  {
    title: "Acesso gratuito",
    label: "Para começar",
    description: "Conteúdos abertos, materiais iniciais e recursos para entender melhor a área de QA.",
    features: ["Conteúdos introdutórios", "Materiais gratuitos", "Primeiro contato com QA"],
    to: "/beneficios",
    cta: "Ver benefícios",
  },
  {
    title: "Plano PRO",
    label: "Para evoluir",
    description: "Acesso a recursos, benefícios, conteúdos e ferramentas para estudar com mais consistência.",
    features: ["Ferramentas e materiais", "Organização de estudos", "Recursos premium"],
    to: "/assinaturas",
    cta: "Ver planos",
    featured: true,
  },
  {
    title: "Mentoria",
    label: "Para ter direção",
    description: "Acompanhamento individual para quem quer clareza, plano, currículo e próximos passos.",
    features: ["Atendimento individual", "Plano personalizado", "Revisão de carreira"],
    to: "/mentoria",
    cta: "Conhecer mentoria",
  },
];

const ebookHighlights = [
  { title: "Manual do QA Iniciante", description: "Fundamentos, planejamento, testes e reporte de bugs." },
  { title: "Excelência em Testes", description: "Estratégia, senso crítico e tomada de decisão em QA." },
  { title: "Robot Framework", description: "Trilha enxuta para automação com mais contexto." },
];

const toolHighlights = [
  {
    title: PAID_PRODUCTS[0]?.title || "Gerador de Testes com IA",
    description: "Cobertura, risco e direcionamento técnico a partir do requisito.",
  },
  {
    title: PAID_PRODUCTS[1]?.title || "Matriz de Casos com IA",
    description: "Passos, resultados esperados e prioridade de execução.",
  },
  {
    title: PAID_PRODUCTS[2]?.title || "Guia de Release com IA",
    description: "Fluxo de validação mais consistente na entrega.",
  },
  {
    title: "Templates e checklists",
    description: "Materiais para organizar rotina, estudos, bugs, releases e evolução profissional.",
  },
];

const featuredYoutubeVideo = {
  id: "pD1SRkGD3gY",
  title: "Conteúdo em vídeo para estudar QA com mais contexto",
  description:
    "Assista ao conteúdo do canal para entender conceitos, carreira e prática de QA com explicações diretas antes de escolher seu próximo passo.",
  url: "https://youtu.be/pD1SRkGD3gY?is=HvgZ56UpItr_DrBc",
};

const authorityStats = [
  { label: "Páginas Kindle lidas", value: "11.156+", width: "100%", color: C.accent },
  { label: "Versões digitais distribuídas", value: "210+", width: "76%", color: C.accent2 },
];

const homeFaq = [
  {
    question: "Dá pra aprender isso de graça no YouTube?",
    answer:
      "Dá, e vale a pena começar por aí. A diferença é o tempo: o QA Iniciante entrega um plano pronto e ferramentas de IA que geram o artefato em minutos, em vez de você juntar sozinho o que está espalhado em vídeos e fóruns.",
  },
  {
    question: "Não conheço essa pessoa/empresa, como confio?",
    answer:
      "O QA Iniciante é conduzido pelo Rogério Jordão, QA Engineer que também mantém o canal @sr.jordao no YouTube com conteúdo público. Você pode conferir o trabalho antes de decidir - link no rodapé desta página.",
  },
  {
    question: "E se eu assinar e não servir pra mim?",
    answer:
      "Nas assinaturas (Starter e PRO) não há fidelidade - cancele quando quiser em \"Meu perfil\". Na mentoria e na revisão de currículo, existe garantia de 7 dias de devolução.",
  },
  {
    question: "Vou pensar e comprar depois - perco alguma coisa?",
    answer:
      "Não existe prazo artificial nas assinaturas. A agenda da mentoria fica aberta o ano todo, então você pode decidir com calma.",
  },
];

const footerLinks = [
  { label: "E-books", to: "/ebooks" },
  { label: "Ferramentas", to: "/produtos" },
  { label: "Planos", to: "/assinaturas" },
  { label: "Mentoria", to: "/mentoria" },
  { label: "Benefícios", to: "/beneficios" },
  { label: "Sobre", to: "/sobre" },
  { label: "FAQ", to: "/faq" },
  { label: "Contato", to: "/contato" },
  { label: "Termos de Uso", to: "/termos-de-uso" },
  { label: "Política de Privacidade", to: "/politica-privacidade" },
];

const socialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/rogeriojordao/", icon: Linkedin },
  { label: "TikTok", href: "https://www.tiktok.com/@qainiciante", icon: Music },
  { label: "Instagram", href: "https://www.instagram.com/qainiciante/", icon: MessageCircle },
  { label: "YouTube", href: "https://www.youtube.com/@sr.jordao", icon: Youtube },
  { label: "Linktree", href: "https://linktr.ee/rogeriojordao", icon: GitHub },
];

// ─── GLOBAL OVERRIDES ──────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  body { background: ${C.bg}; }
`;

// ─── SHARED PRIMITIVES ────────────────────────────────────────────────────
const Eyebrow = styled.div`
  font-family: ${C.mono};
  font-size: 10.5px;
  font-weight: 800;
  color: ${({ $color }) => $color || C.accent};
  letter-spacing: 0.13em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  &::before {
    content: "";
    width: 18px;
    height: 2px;
    border-radius: 999px;
    background: ${({ $color }) => $color || C.accent};
    flex-shrink: 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${C.display};
  font-size: clamp(1.45rem, 2.4vw, 2rem);
  font-weight: 700;
  color: ${C.textPrimary};
  letter-spacing: -0.035em;
  line-height: 1.18;
  max-width: ${({ $max }) => $max || "640px"};
  margin: 0;

  span {
    color: ${C.accent};
  }
`;

const SectionDesc = styled.p`
  max-width: ${({ $max }) => $max || "620px"};
  color: ${C.textMuted};
  line-height: 1.72;
  font-size: 0.95rem;
  margin: 12px 0 0;
`;

const TestimonialsIntro = styled.p`
  margin: 40px 0 18px;
  color: ${C.textPrimary};
  font-weight: 700;
  font-size: 1rem;
`;

const FaqList = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 8px;
`;

const FaqItem = styled.div`
  padding: 18px 20px;
  border: 1px solid ${C.border};
  border-radius: 14px;
  background: ${C.card};
`;

const FaqQuestion = styled.p`
  margin: 0 0 6px;
  color: ${C.textPrimary};
  font-weight: 800;
  font-size: 0.98rem;
`;

const FaqAnswer = styled.p`
  margin: 0;
  color: ${C.textSecondary};
  line-height: 1.65;
  font-size: 0.92rem;
`;

const SectionHeader = styled.div`
  margin-bottom: 28px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 18px;

  @media (max-width: 720px) {
    display: block;
  }
`;

const LinkBtn = styled(Link)`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  background: rgba(148, 163, 184, 0.07);
  border: 1px solid ${C.border};
  color: ${C.textSecondary};
  font-size: 0.82rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  text-decoration: none;
  transition: border-color 0.18s, color 0.18s, transform 0.18s;

  &:hover {
    border-color: ${C.borderHover};
    color: ${C.accent};
    transform: translateY(-1px);
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
`;

const BtnPrimary = styled(Link)`
  min-height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  background: ${({ $color }) => $color || C.accent};
  color: #07111f;
  font-weight: 800;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  transition: opacity 0.18s, transform 0.18s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const BtnSecondary = styled(Link)`
  min-height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  background: rgba(31, 48, 80, 0.55);
  color: #dbe6f5;
  font-weight: 700;
  font-size: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  transition: border-color 0.18s, transform 0.18s;

  &:hover {
    border-color: rgba(148, 163, 184, 0.44);
    transform: translateY(-1px);
  }
`;

// ─── HERO / CAROUSEL ──────────────────────────────────────────────────────
const HeroWrap = styled.section`
  padding: 34px 32px 44px;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &::before {
    content: "";
    position: absolute;
    inset: -140px -80px auto auto;
    width: 560px;
    height: 560px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(34, 184, 209, 0.10) 0%, transparent 68%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    left: -160px;
    bottom: -220px;
    width: 520px;
    height: 520px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(108, 143, 176, 0.09) 0%, transparent 64%);
    pointer-events: none;
  }

  @media (max-width: 640px) {
    padding: 28px 20px 36px;
  }
`;

const CarouselShell = styled.div`
  position: relative;
  z-index: 1;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 26px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(31, 48, 80, 0.92), rgba(10, 17, 30, 0.96)),
    radial-gradient(circle at top right, rgba(34, 184, 209, 0.16), transparent 35%);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
`;

const BannerSlide = styled.article`
  min-width: 100%;
  padding: 44px;
  min-height: 460px;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 340px;
  gap: 34px;
  align-items: center;
  position: relative;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    min-height: unset;
  }

  @media (max-width: 640px) {
    padding: 28px 22px;
  }
`;

const HeroContent = styled.div`
  max-width: 680px;
`;

const HeroTitle = styled.h1`
  font-family: ${C.display};
  font-size: clamp(1.9rem, 4.2vw, 3.7rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${C.textPrimary};
  letter-spacing: -0.01em;
  margin: 0 0 18px;

  span {
    color: ${({ $color }) => $color || C.accent};
  }
`;

const HeroDesc = styled.p`
  font-size: 1.02rem;
  line-height: 1.74;
  color: ${C.textMuted};
  max-width: 590px;
  margin: 0 0 24px;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 26px 0 0;
`;

const HeroBullets = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HeroPill = styled.span`
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(31, 48, 80, 0.68);
  color: #dff6fa;
  font-size: 0.78rem;
  font-weight: 750;
  display: inline-flex;
  align-items: center;
  gap: 7px;

  svg {
    color: ${({ $color }) => $color || C.accent};
  }
`;

const HeroVisual = styled.div`
  display: grid;
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 160px 1fr;
    align-items: stretch;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const PortraitCard = styled.div`
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(31, 48, 80, 0.75);
  overflow: hidden;
  position: relative;
`;

const PortraitImg = styled.img`
  width: 100%;
  display: block;
  filter: grayscale(8%);

  @media (max-width: 900px) {
    height: 100%;
    object-fit: cover;
  }
`;

const PortraitOverlay = styled.div`
  position: absolute;
  inset: auto 0 0;
  padding: 18px;
  background: linear-gradient(transparent, rgba(10, 17, 30, 0.94));
`;

const PortraitName = styled.p`
  margin: 0 0 3px;
  font-weight: 800;
  color: ${C.textPrimary};
`;

const PortraitRole = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${C.textMuted};
  font-family: ${C.mono};
`;

const HeroInfoCard = styled.div`
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.13);
  background: rgba(10, 17, 30, 0.42);
  padding: 18px;
  display: grid;
  gap: 12px;
`;

const HeroInfoTop = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #07111f;
  background: ${({ $color }) => $color || C.accent};
`;

const HeroInfoTitle = styled.p`
  margin: 0;
  color: ${C.textPrimary};
  font-weight: 850;
  font-size: 1rem;
`;

const HeroInfoText = styled.p`
  margin: 0;
  color: ${C.textMuted};
  line-height: 1.58;
  font-size: 0.84rem;
`;

const QuickChips = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
`;

const Chip = styled.span`
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${C.accentBorder};
  background: ${C.accentDim};
  color: #8FE3F2;
  font-size: 0.76rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: ${C.mono};
`;

const ChipDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${C.accent};
  flex-shrink: 0;
`;

// ─── SECTION WRAPPER ──────────────────────────────────────────────────────
const Section = styled.section`
  padding: 54px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;

  @media (max-width: 640px) {
    padding: 42px 20px;
  }
`;

const SoftSection = styled(Section)`
  background:
    radial-gradient(circle at top left, rgba(34, 184, 209, 0.07), transparent 36%),
    rgba(31, 48, 80, 0.28);
`;

// ─── NAV CARDS ────────────────────────────────────────────────────────────
const NavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const NavCardTitle = styled.p`
  margin: 0 0 8px;
  font-weight: 850;
  font-size: 1rem;
  color: #dbe6f5;
  transition: color 0.18s;
`;

const NavCard = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(180deg, rgba(31, 48, 80, 0.88), rgba(31, 48, 80, 0.58));
  border: 1px solid ${C.border};
  border-radius: 18px;
  padding: 20px;
  min-height: 218px;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s, transform 0.2s, background 0.2s;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(34, 184, 209, 0.10), transparent 40%);
    opacity: 0;
    transition: opacity 0.22s ease;
  }

  &:hover {
    border-color: ${C.borderHover};
    transform: translateY(-3px);
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover ${NavCardTitle} {
    color: #8FE3F2;
  }
`;

const NavCardTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
`;

const NavCardIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: ${C.accentDim};
  border: 1px solid ${C.accentBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C.accent};
  flex-shrink: 0;
`;

const NavCardTag = styled.span`
  font-family: ${C.mono};
  font-size: 0.67rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${C.textSoft};
  border: 1px solid rgba(148, 163, 184, 0.10);
  border-radius: 999px;
  padding: 6px 8px;
`;

const NavCardDesc = styled.p`
  position: relative;
  z-index: 1;
  margin: 0 0 18px;
  font-size: 0.84rem;
  line-height: 1.65;
  color: ${C.textDimmer};
  flex: 1;
`;

const NavCardCta = styled.span`
  position: relative;
  z-index: 1;
  font-size: 0.78rem;
  font-weight: 850;
  color: ${C.accent};
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: ${C.mono};
`;

// ─── JOURNEY ──────────────────────────────────────────────────────────────
const JourneyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1px solid ${C.border};
  border-radius: 20px;
  overflow: hidden;
  background: rgba(10, 17, 30, 0.22);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const JourneyStep = styled.div`
  padding: 26px 22px;
  border-right: 1px solid ${C.border};

  &:last-child {
    border-right: none;
  }

  @media (max-width: 900px) {
    &:nth-child(2) {
      border-right: none;
    }
    &:nth-child(1), &:nth-child(2) {
      border-bottom: 1px solid ${C.border};
    }
  }

  @media (max-width: 560px) {
    border-right: none;
    border-bottom: 1px solid ${C.border};

    &:last-child {
      border-bottom: none;
    }
  }
`;

const JourneyNum = styled.div`
  font-family: ${C.mono};
  font-size: 2.1rem;
  font-weight: 900;
  color: rgba(34, 184, 209, 0.16);
  line-height: 1;
  margin-bottom: 12px;
`;

const JourneyTitle = styled.p`
  margin: 0 0 8px;
  font-weight: 850;
  font-size: 1rem;
  color: ${C.textPrimary};
`;

const JourneyDesc = styled.p`
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.66;
  color: ${C.textDimmer};
`;

// ─── SERVICE HIGHLIGHTS ───────────────────────────────────────────────────
const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  border: 1px solid ${({ $featured }) => ($featured ? C.accentBorder : C.border)};
  border-radius: 22px;
  background:
    radial-gradient(circle at top right, ${({ $featured }) => ($featured ? "rgba(34, 184, 209, 0.14)" : "rgba(108, 143, 176, 0.10)")}, transparent 38%),
    rgba(31, 48, 80, 0.60);
  padding: 28px;
`;

const ServiceTop = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 18px;
`;

const ServiceIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: ${({ $color }) => $color || C.accent};
  color: #07111f;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ServiceTitle = styled.h3`
  font-family: ${C.display};
  margin: 0 0 4px;
  color: ${C.textPrimary};
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.005em;
`;

const ServiceSub = styled.p`
  margin: 0;
  color: ${C.textSoft};
  font-family: ${C.mono};
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ServiceText = styled.p`
  margin: 0 0 18px;
  color: ${C.textMuted};
  font-size: 0.92rem;
  line-height: 1.7;
`;

const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
`;

const CheckItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  color: ${C.textSecondary};
  line-height: 1.48;
  font-size: 0.86rem;

  svg {
    color: ${({ $color }) => $color || C.accent};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

// ─── PLANS ────────────────────────────────────────────────────────────────
const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  border: 1px solid ${({ $featured }) => ($featured ? C.accentBorder : C.border)};
  border-radius: 20px;
  background: ${({ $featured }) =>
    $featured
      ? "linear-gradient(180deg, rgba(34, 184, 209, 0.12), rgba(31, 48, 80, 0.56))"
      : "rgba(31, 48, 80, 0.48)"};
  padding: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $featured }) => ($featured ? "radial-gradient(circle at top right, rgba(34, 184, 209, 0.16), transparent 44%)" : "none")};
    pointer-events: none;
  }
`;

const PlanBadge = styled.span`
  position: relative;
  display: inline-flex;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  align-items: center;
  background: rgba(34, 184, 209, 0.10);
  border: 1px solid rgba(34, 184, 209, 0.15);
  color: #8FE3F2;
  font-size: 0.72rem;
  font-family: ${C.mono};
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
`;

const PlanTitle = styled.h3`
  font-family: ${C.display};
  position: relative;
  margin: 0 0 8px;
  color: ${C.textPrimary};
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.005em;
`;

const PlanDesc = styled.p`
  position: relative;
  margin: 0 0 18px;
  color: ${C.textDimmer};
  font-size: 0.86rem;
  line-height: 1.65;
`;

const PlanFeatures = styled.div`
  position: relative;
  display: grid;
  gap: 9px;
  margin-bottom: 20px;
`;

const PlanFeature = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: ${C.textSecondary};
  font-size: 0.84rem;

  svg {
    color: ${C.accent};
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

// ─── SHOWCASE ─────────────────────────────────────────────────────────────
const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const ShowcasePanel = styled.div`
  background: ${C.card};
  border: 1px solid ${C.border};
  border-radius: 20px;
  padding: 26px;
  display: flex;
  flex-direction: column;
`;

const ShowcasePanelTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.18rem;
  font-weight: 850;
  color: ${C.textPrimary};
`;

const ShowcasePanelDesc = styled.p`
  margin: 0;
  font-size: 0.86rem;
  color: ${C.textDimmer};
  line-height: 1.65;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
`;

const ShowcaseList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ShowcaseItem = styled.div`
  padding: 13px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.07);

  &:last-child {
    border-bottom: none;
  }
`;

const ShowcaseItemTitle = styled.p`
  margin: 0 0 4px;
  font-size: 0.88rem;
  font-weight: 750;
  color: #c7d2e8;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(34, 184, 209, 0.58);
    flex-shrink: 0;
  }
`;

const ShowcaseItemDesc = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${C.textSoft};
  line-height: 1.55;
  padding-left: 14px;
`;

// ─── YOUTUBE ──────────────────────────────────────────────────────────────
const YoutubeFeature = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.35fr);
  gap: 22px;
  align-items: center;
  border: 1px solid rgba(255, 0, 51, 0.18);
  border-radius: 24px;
  background:
    radial-gradient(circle at top left, rgba(255, 0, 51, 0.12), transparent 34%),
    rgba(31, 48, 80, 0.58);
  padding: 24px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const YoutubeCopy = styled.div`
  display: grid;
  gap: 14px;
`;

const YoutubeTitle = styled.h3`
  font-family: ${C.display};
  margin: 0;
  color: ${C.textPrimary};
  font-weight: 700;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
  line-height: 1.2;
  letter-spacing: -0.005em;
`;

const YoutubeText = styled.p`
  margin: 0;
  color: ${C.textMuted};
  font-size: 0.94rem;
  line-height: 1.72;
`;

const YoutubeActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const YoutubeButton = styled.a`
  min-height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(255, 0, 51, 0.34);
  background: rgba(255, 0, 51, 0.12);
  color: #fecdd3;
  font-size: 0.9rem;
  font-weight: 800;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: border-color 0.18s, transform 0.18s, background 0.18s;

  &:hover {
    border-color: rgba(255, 0, 51, 0.58);
    background: rgba(255, 0, 51, 0.18);
    transform: translateY(-1px);
  }
`;

const YoutubeFrameWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: #0B1424;
  box-shadow: 0 22px 56px rgba(0, 0, 0, 0.28);
`;

const YoutubeFrame = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

// ─── AUTHORITY / ABOUT ────────────────────────────────────────────────────
const AuthorityGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 18px;
  align-items: stretch;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const AuthorityCard = styled.div`
  background: rgba(10, 17, 30, 0.38);
  border: 1px solid ${C.border};
  border-radius: 20px;
  padding: 24px;
`;

const ChartRows = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 18px;
`;

const ChartRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  font-size: 0.8rem;
  color: ${C.textMuted};
  margin-bottom: 6px;

  span:last-child {
    font-weight: 900;
    font-family: ${C.mono};
    color: ${C.textPrimary};
  }
`;

const ChartTrack = styled.div`
  height: 7px;
  background: rgba(148, 163, 184, 0.10);
  border-radius: 999px;
  overflow: hidden;
`;

const ChartFill = styled.div`
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => $width || "60%"};
  background: ${({ $color }) => $color || C.accent};
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: 28px;
  align-items: start;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const AboutPhoto = styled.img`
  width: 100%;
  border-radius: 18px;
  border: 1px solid ${C.accentBorder};
  display: block;
`;

const AboutText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.76;
  color: ${C.textMuted};
`;

// ─── CTA ──────────────────────────────────────────────────────────────────
const FinalCta = styled.section`
  margin: 54px 32px 24px;
  border-radius: 26px;
  padding: 34px;
  border: 1px solid ${C.accentBorder};
  background:
    radial-gradient(circle at top right, rgba(34, 184, 209, 0.20), transparent 36%),
    linear-gradient(135deg, rgba(31, 48, 80, 0.92), rgba(10, 17, 30, 0.96));
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    margin: 42px 20px 20px;
  }
`;

const FinalCtaTitle = styled.h2`
  font-family: ${C.display};
  margin: 0 0 10px;
  color: ${C.textPrimary};
  font-weight: 700;
  font-size: clamp(1.45rem, 2.8vw, 2.2rem);
  letter-spacing: -0.005em;
`;

const FinalCtaText = styled.p`
  margin: 0;
  color: ${C.textMuted};
  line-height: 1.65;
  max-width: 680px;
`;

// ─── FOOTER ───────────────────────────────────────────────────────────────
const FooterWrap = styled.footer`
  padding: 32px 32px 0;
  display: grid;
  gap: 14px;

  @media (max-width: 640px) {
    padding: 28px 20px 0;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FooterLink = styled(Link)`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  background: ${C.surface};
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  font-size: 0.82rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: border-color 0.18s, color 0.18s;

  &:hover {
    border-color: rgba(148, 163, 184, 0.28);
    color: ${C.textSecondary};
  }
`;

const FooterExt = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FooterExtLink = styled.a`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.08);
  color: ${C.textDimmer};
  font-size: 0.82rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  text-decoration: none;
  transition: color 0.18s, border-color 0.18s;

  &:hover {
    color: ${C.textSecondary};
    border-color: rgba(148, 163, 184, 0.20);
  }
`;

const FooterText = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: ${C.textSoft};
  line-height: 1.65;
`;

// ─── PAGE ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const HeroIcon = heroMain.icon;
  const heroVariant = getVariant("home_hero_headline");
  const heroHeadline = heroHeadlineVariants[heroVariant] || heroHeadlineVariants.control;
  const highlightIdx = heroHeadline.title.indexOf(heroHeadline.highlight);
  const heroTitleParts =
    highlightIdx >= 0
      ? {
          before: heroHeadline.title.slice(0, highlightIdx),
          highlight: heroHeadline.highlight,
          after: heroHeadline.title.slice(highlightIdx + heroHeadline.highlight.length),
        }
      : { before: heroHeadline.title, highlight: "", after: "" };

  return (
    <SitePage>
      <GlobalStyle />
      <SiteShell>
        {/* HERO */}
        <HeroWrap>

          <CarouselShell>
            <BannerSlide>
              <HeroContent>
                <Eyebrow $color={heroMain.color}>{heroMain.eyebrow}</Eyebrow>
                <HeroTitle $color={heroMain.color}>
                  {heroTitleParts.before}<span>{heroTitleParts.highlight}</span>{heroTitleParts.after}
                </HeroTitle>
                <HeroDesc>{heroHeadline.description}</HeroDesc>

                <HeroBullets>
                  {heroMain.bullets.map((bullet) => (
                    <HeroPill key={bullet} $color={heroMain.color}>
                      <CheckCircle size={14} />
                      {bullet}
                    </HeroPill>
                  ))}
                </HeroBullets>

                <HeroActions>
                  <BtnPrimary
                    to={heroMain.to}
                    $color={heroMain.color}
                    onClick={() => trackEvent("cta_click", { source: "home_hero", target: heroMain.to })}
                  >
                    {heroMain.cta}
                    <ArrowRight size={15} />
                  </BtnPrimary>
                  <BtnSecondary
                    to={heroMain.secondaryTo}
                    onClick={() => trackEvent("cta_click", { source: "home_hero", target: heroMain.secondaryTo })}
                  >
                    {heroMain.secondaryCta}
                  </BtnSecondary>
                </HeroActions>
              </HeroContent>

              <HeroVisual>
                <PortraitCard>
                  <PortraitImg src={rogerioImg} alt="Rogério Jordão" />
                  <PortraitOverlay>
                    <PortraitName>Rogério Jordão</PortraitName>
                    <PortraitRole>QA Engineer &amp; Mentor</PortraitRole>
                  </PortraitOverlay>
                </PortraitCard>

                <HeroInfoCard>
                  <HeroInfoTop $color={heroMain.color}>
                    <HeroIcon size={22} />
                  </HeroInfoTop>
                  <div>
                    <HeroInfoTitle>Do zero à prática com mais clareza.</HeroInfoTitle>
                    <HeroInfoText>
                      Acesse mentoria, revisão de currículo, planos, ferramentas, e-books e comunidade em um único lugar, desde o primeiro acesso.
                    </HeroInfoText>
                  </div>
                </HeroInfoCard>
              </HeroVisual>
            </BannerSlide>
          </CarouselShell>

          <QuickChips>
            {quickHighlights.map((item) => (
              <Chip key={item}>
                <ChipDot />
                {item}
              </Chip>
            ))}
          </QuickChips>
        </HeroWrap>

        {/* VISÃO GERAL */}
        <Section>
          <SectionHeader>
            <div>
              <Eyebrow>O que você encontra aqui</Eyebrow>
              <SectionTitle>Tudo que o QA Iniciante oferece em um só lugar.</SectionTitle>
              <SectionDesc>
                Navegue pelos principais caminhos para sua evolução: estudo, prática, carreira, planos e acompanhamento individual.
              </SectionDesc>
            </div>
            <LinkBtn to="/assinaturas">
              Ver planos <ArrowRight size={12} />
            </LinkBtn>
          </SectionHeader>

          <NavGrid>
            {navigationCards.map(({ title, description, to, icon: Icon, tag }) => (
              <NavCard key={title} to={to}>
                <NavCardTop>
                  <NavCardIcon>
                    <Icon size={19} />
                  </NavCardIcon>
                  <NavCardTag>{tag}</NavCardTag>
                </NavCardTop>
                <NavCardTitle>{title}</NavCardTitle>
                <NavCardDesc>{description}</NavCardDesc>
                <NavCardCta>
                  Abrir área <ArrowRight size={12} />
                </NavCardCta>
              </NavCard>
            ))}
          </NavGrid>
        </Section>

        {/* JORNADA */}
        <Section>
          <SectionHeader>
            <div>
              <Eyebrow>Jornada de aprendizado</Eyebrow>
              <SectionTitle>Uma trilha simples para sair da confusão e evoluir com ordem.</SectionTitle>
              <SectionDesc>
                O QA Iniciante oferece mais do que conteúdo solto: é uma jornada estruturada para sua evolução profissional.
              </SectionDesc>
            </div>
          </SectionHeader>

          <JourneyGrid>
            {journeySteps.map((step) => (
              <JourneyStep key={step.num}>
                <JourneyNum>{step.num}</JourneyNum>
                <JourneyTitle>{step.title}</JourneyTitle>
                <JourneyDesc>{step.description}</JourneyDesc>
              </JourneyStep>
            ))}
          </JourneyGrid>
        </Section>

        {/* MENTORIA E REVISÃO */}
        <SoftSection>
          <SectionHeader>
            <div>
              <Eyebrow>Mentoria e carreira</Eyebrow>
              <SectionTitle>Além de conteúdo, direção para estudar, praticar e se posicionar melhor.</SectionTitle>
              <SectionDesc>
                Receba direção com mentoria individual e prepare seu currículo para se destacar em vagas de QA.
              </SectionDesc>
            </div>
          </SectionHeader>

          <SplitGrid>
            <ServiceCard $featured>
              <ServiceTop>
                <ServiceIcon $color={C.accent}>
                  <Calendar size={22} />
                </ServiceIcon>
                <div>
                  <ServiceTitle>Mentoria QA Iniciante</ServiceTitle>
                  <ServiceSub>Plano, prática e direção</ServiceSub>
                </div>
              </ServiceTop>
              <ServiceText>
                Para quem quer entrar ou evoluir em QA com acompanhamento individual, clareza de próximos passos e um plano possível de executar.
              </ServiceText>
              <CheckList>
                {mentorshipBenefits.map((item) => (
                  <CheckItem key={item} $color={C.accent}>
                    <CheckCircle size={15} />
                    {item}
                  </CheckItem>
                ))}
              </CheckList>
              <ActionRow>
                <BtnPrimary to="/mentoria">Quero mentoria <ArrowRight size={15} /></BtnPrimary>
              </ActionRow>
            </ServiceCard>

            <ServiceCard>
              <ServiceTop>
                <ServiceIcon $color={C.accent2}>
                  <FileText size={22} />
                </ServiceIcon>
                <div>
                  <ServiceTitle>Revisão de currículo</ServiceTitle>
                  <ServiceSub>Currículo, LinkedIn e perfil</ServiceSub>
                </div>
              </ServiceTop>
              <ServiceText>
                Para transformar suas experiências, estudos e projetos em uma apresentação mais clara, profissional e alinhada com oportunidades de QA.
              </ServiceText>
              <CheckList>
                {resumeBenefits.map((item) => (
                  <CheckItem key={item} $color={C.accent2}>
                    <CheckCircle size={15} />
                    {item}
                  </CheckItem>
                ))}
              </CheckList>
              <ActionRow>
                <BtnSecondary to="/revisao-curriculo">Solicitar revisão <ArrowRight size={15} /></BtnSecondary>
              </ActionRow>
            </ServiceCard>
          </SplitGrid>

          <TestimonialsIntro>Resultados reais de quem já passou pela mentoria.</TestimonialsIntro>
          <TestimonialsGrid />
        </SoftSection>

        {/* PLANOS */}
        <Section>
          <SectionHeader>
            <div>
              <Eyebrow>Planos</Eyebrow>
              <SectionTitle>Planos flexíveis para cada etapa da sua jornada.</SectionTitle>
              <SectionDesc>
                Seja você um iniciante buscando uma base, um profissional querendo evoluir ou alguém que precisa de direcionamento individual, temos um plano ideal para você.
              </SectionDesc>
            </div>
            <LinkBtn to="/assinaturas">
              Comparar planos <ArrowRight size={12} />
            </LinkBtn>
          </SectionHeader>

          <PlansGrid>
            {planCards.map((plan) => (
              <PlanCard key={plan.title} $featured={plan.featured}>
                <PlanBadge>{plan.label}</PlanBadge>
                <PlanTitle>{plan.title}</PlanTitle>
                <PlanDesc>{plan.description}</PlanDesc>
                <PlanFeatures>
                  {plan.features.map((feature) => (
                    <PlanFeature key={feature}>
                      <CheckCircle size={14} />
                      {feature}
                    </PlanFeature>
                  ))}
                </PlanFeatures>
                {plan.featured ? (
                  <BtnPrimary to={plan.to}>{plan.cta} <ArrowRight size={15} /></BtnPrimary>
                ) : (
                  <BtnSecondary to={plan.to}>{plan.cta} <ArrowRight size={15} /></BtnSecondary>
                )}
              </PlanCard>
            ))}
          </PlansGrid>
        </Section>

        {/* CONTEÚDOS E FERRAMENTAS */}
        <SoftSection>
          <SectionHeader>
            <div>
              <Eyebrow>Conteúdo e prática</Eyebrow>
              <SectionTitle>Estudo e prática caminham juntos.</SectionTitle>
              <SectionDesc>
                Construa uma base sólida com nossos e-books e aplique seu conhecimento com ferramentas inteligentes que aceleram seu desenvolvimento.
              </SectionDesc>
            </div>
          </SectionHeader>

          <ShowcaseGrid>
            <ShowcasePanel>
              <ShowcasePanelTitle>Biblioteca de e-books</ShowcasePanelTitle>
              <ShowcasePanelDesc>
                Leituras para construir base, ampliar repertório técnico e revisar temas importantes no seu tempo.
              </ShowcasePanelDesc>
              <ShowcaseList>
                {ebookHighlights.map((item) => (
                  <ShowcaseItem key={item.title}>
                    <ShowcaseItemTitle>{item.title}</ShowcaseItemTitle>
                    <ShowcaseItemDesc>{item.description}</ShowcaseItemDesc>
                  </ShowcaseItem>
                ))}
              </ShowcaseList>
              <ActionRow>
                <LinkBtn to="/ebooks">
                  Ver biblioteca <ArrowRight size={12} />
                </LinkBtn>
              </ActionRow>
            </ShowcasePanel>

            <ShowcasePanel>
              <ShowcasePanelTitle>Ferramentas e Central QA</ShowcasePanelTitle>
              <ShowcasePanelDesc>
                Recursos para transformar teoria em entregas concretas, organizar rotina e produzir artefatos com IA.
              </ShowcasePanelDesc>
              <ShowcaseList>
                {toolHighlights.map((item) => (
                  <ShowcaseItem key={item.title}>
                    <ShowcaseItemTitle>{item.title}</ShowcaseItemTitle>
                    <ShowcaseItemDesc>{item.description}</ShowcaseItemDesc>
                  </ShowcaseItem>
                ))}
              </ShowcaseList>
              <ActionRow>
                <LinkBtn to="/produtos">
                  Ver ferramentas <ArrowRight size={12} />
                </LinkBtn>
                <LinkBtn to="/workspace-qa">
                  Abrir Central QA <ArrowRight size={12} />
                </LinkBtn>
              </ActionRow>
            </ShowcasePanel>
          </ShowcaseGrid>
        </SoftSection>

        {/* YOUTUBE */}
        <Section>
          <SectionHeader>
            <div>
              <Eyebrow $color="#ff0033">Canal no YouTube</Eyebrow>
              <SectionTitle>Aprenda QA também pelo meu canal.</SectionTitle>
              <SectionDesc>
                Conteúdo em vídeo para reforçar conceitos, carreira e prática com explicações objetivas para quem está começando ou organizando a evolução.
              </SectionDesc>
            </div>
          </SectionHeader>

          <YoutubeFeature>
            <YoutubeCopy>
              <YoutubeTitle>{featuredYoutubeVideo.title}</YoutubeTitle>
              <YoutubeText>{featuredYoutubeVideo.description}</YoutubeText>
              <YoutubeActions>
                <YoutubeButton href={featuredYoutubeVideo.url} target="_blank" rel="noreferrer">
                  <Youtube size={17} />
                  Assistir no YouTube
                </YoutubeButton>
              </YoutubeActions>
            </YoutubeCopy>

            <YoutubeFrameWrap>
              <YoutubeFrame
                src={`https://www.youtube.com/embed/${featuredYoutubeVideo.id}`}
                title={featuredYoutubeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </YoutubeFrameWrap>
          </YoutubeFeature>
        </Section>

        {/* AUTORIDADE */}
        <Section>
          <AuthorityGrid>
            <AuthorityCard>
              <Eyebrow>Autoridade</Eyebrow>
              <SectionTitle $max="520px">Conteúdo criado por quem vive qualidade de software na prática.</SectionTitle>
              <SectionDesc>
                O QA Iniciante une experiência real em QA, materiais autorais, comunidade e orientação para quem precisa de clareza no começo da jornada.
              </SectionDesc>

              <ChartRows>
                {authorityStats.map((stat) => (
                  <div key={stat.label}>
                    <ChartRowHeader>
                      <span>{stat.label}</span>
                      <span>{stat.value}</span>
                    </ChartRowHeader>
                    <ChartTrack>
                      <ChartFill $width={stat.width} $color={stat.color} />
                    </ChartTrack>
                  </div>
                ))}
              </ChartRows>
            </AuthorityCard>

            <AuthorityCard>
              <Eyebrow>Sobre o projeto</Eyebrow>
              <AboutGrid>
                <AboutPhoto src={rogerioImg} alt="Rogério Jordão" />
                <div>
                  <AboutText>
                    Sou Rogério Jordão, profissional de Qualidade de Software com experiência em testes, automação e melhoria da qualidade em produtos digitais. Criei o QA Iniciante para ajudar pessoas a entrarem e evoluírem na área com uma estrutura mais prática, organizada e útil no dia a dia.
                  </AboutText>
                  <ActionRow>
                    <LinkBtn to="/sobre">
                      Conhecer o projeto <ArrowRight size={12} />
                    </LinkBtn>
                    <LinkBtn to="/mentoria">
                      Ver mentoria <ArrowRight size={12} />
                    </LinkBtn>
                  </ActionRow>
                </div>
              </AboutGrid>
            </AuthorityCard>
          </AuthorityGrid>
        </Section>

        {/* PERGUNTAS ANTES DE DECIDIR */}
        <Section>
          <SectionHeader>
            <div>
              <Eyebrow>Perguntas antes de decidir</Eyebrow>
              <SectionTitle>As dúvidas mais comuns de quem chega até aqui.</SectionTitle>
            </div>
            <LinkBtn to="/faq">
              Ver FAQ completo <ArrowRight size={12} />
            </LinkBtn>
          </SectionHeader>

          <FaqList>
            {homeFaq.map((item) => (
              <FaqItem key={item.question}>
                <FaqQuestion>{item.question}</FaqQuestion>
                <FaqAnswer>{item.answer}</FaqAnswer>
              </FaqItem>
            ))}
          </FaqList>
        </Section>

        {/* CTA FINAL */}
        <FinalCta>
          <div>
            <FinalCtaTitle>Comece pelo caminho mais simples para o seu momento.</FinalCtaTitle>
            <FinalCtaText>
              Explore os conteúdos gratuitos, veja os planos, use as ferramentas ou escolha a mentoria se você precisa de direção individual para evoluir em QA.
            </FinalCtaText>
          </div>
          <ActionRow style={{ marginTop: 0, justifyContent: "flex-end" }}>
            <BtnPrimary
              to={heroMain.to}
              onClick={() => trackEvent("cta_click", { source: "home_final", target: heroMain.to })}
            >
              {heroMain.cta} <ArrowRight size={15} />
            </BtnPrimary>
            <BtnSecondary to="/assinaturas">Ver planos</BtnSecondary>
          </ActionRow>
        </FinalCta>

        {/* FOOTER */}
        <FooterWrap>
          <FooterLinks>
            {footerLinks.map((link) => (
              <FooterLink key={link.to} to={link.to}>
                {link.label}
              </FooterLink>
            ))}
          </FooterLinks>

          <FooterExt>
            {socialLinks.map(({ label, href, icon: Icon }) => (
              <FooterExtLink key={href} href={href} target="_blank" rel="noreferrer">
                <Icon size={14} />
                {label}
              </FooterExtLink>
            ))}
          </FooterExt>

          <FooterText>
            QA Iniciante reúne materiais, ferramentas, planos e orientação para quem quer aprender, praticar e evoluir em Qualidade de Software com mais clareza.
          </FooterText>
        </FooterWrap>
      </SiteShell>
    </SitePage>
  );
}
